// ── CANONICAL BOT IDENTITY (God 2026-08-24) ─────────────────────────────────
// ONE name per bot, for the whole system. Before this, the same bot answered to
// a different name in every panel:
//
//   mai_veg_mcx  →  "Rudra Vega MCX"  (Mongo deployments.name)
//                   "VEGA MCX"        (Fleet Registry cards)
//                   "🛢 VEGA MCX"      (AI Audit matrix)
//                   "MAI VEG MCX"     (botLabel() — id with underscores upcased)
//                   "Rudra VEGA MCX"  (fleet_dna.py)
//                   "Rudra Vega Max"  (tara_precalib_audit.py)
//                   "Rudra Vag"       (rudra_formats.py — a typo, for mai_veg)
//
// That drift was not cosmetic. Two live lookups were already broken by it:
//   * tara_precalib_audit.py keyed on bot id "mai_veg_max" — no such bot exists,
//     so the MCX bot was silently never audited.
//   * market_audit.py filtered deployments by name ["Rudra Vega","Rudra Shool",
//     "Rudra Sthambh"] — both MCX bots and both SELL lanes fell out of the audit.
//
// God 2026-08-24 fixed the vocabulary: MAX (not MCX) in the bot's NAME.
// IDs are deliberately UNCHANGED — the ledger's rows, botversions, deployments
// and the registry are all keyed on them, and renaming a key mid-72h-watch would
// orphan history for nothing. A name is a label; the id is the identity.
//
// The `segment` field keeps saying MCX on purpose: it names the EXCHANGE the leg
// trades on (Multi Commodity Exchange), which is a fact about the instrument, not
// a name for the bot. Calling it "MAX OPTIONS" would make the badge wrong.

export interface BotIdentity {
  id: string;          // canonical runtime id — NEVER change, everything keys on it
  legacy: string;      // pre-mai_ id still present in old ledger rows
  name: string;        // the ONE display name (God 2026-08-24)
  icon: string;
  segment: string;     // exchange/instrument the leg trades — factual, not a name
  accent: string;
  model: 'VEG' | 'SHOOL' | 'STHAMBH';
}

export const FLEET: BotIdentity[] = [
  { id: 'mai_veg',          legacy: 'veg',          name: 'Rudra Vega',          icon: '⚡',  segment: 'OPTIONS',      accent: '#a78bfa', model: 'VEG' },
  { id: 'mai_veg_mcx',      legacy: 'veg_mcx',      name: 'Rudra Vega Max',      icon: '🛢️', segment: 'MCX OPTIONS',  accent: '#c084fc', model: 'VEG' },
  { id: 'mai_shool',        legacy: 'shool',        name: 'Rudra Shool',         icon: '🔱',  segment: 'FUTURES',      accent: '#38bdf8', model: 'SHOOL' },
  { id: 'mai_shool_mcx',    legacy: 'shool_mcx',    name: 'Rudra Shool Max',     icon: '⚒️', segment: 'MCX FUTURES',  accent: '#22d3ee', model: 'SHOOL' },
  { id: 'mai_sthambh',      legacy: 'sthambh',      name: 'Rudra Sthambh',       icon: '🏛️', segment: 'CASH BUY',     accent: '#fbbf24', model: 'STHAMBH' },
  { id: 'mai_veg_sail',     legacy: 'veg_sail',     name: 'Rudra Vega Sell',     icon: '🌙',  segment: 'OPTIONS SELL', accent: '#10b981', model: 'VEG' },
  { id: 'mai_veg_mcx_sail', legacy: 'veg_mcx_sail', name: 'Rudra Vega Max Sell', icon: '🌙',  segment: 'MCX OPT SELL', accent: '#38bdf8', model: 'VEG' },
  // God 2026-10-25: NIFTY-50 stock lanes (BOT-08 / BOT-09)
  { id: 'mai_veg_stock',    legacy: 'veg_stock',    name: 'Rudra Vega Stock',    icon: '📈',  segment: 'STOCK OPTIONS', accent: '#f472b6', model: 'VEG' },
  { id: 'mai_shool_stock',  legacy: 'shool_stock',  name: 'Rudra Shool Stock',   icon: '🗡️', segment: 'STOCK FUTURES', accent: '#60a5fa', model: 'SHOOL' },
  // God 2026-11-02: BOT-11 NIFTY-50 stock option SELLING (mirror of Rudra Vega Sell on the stock option chart)
  { id: 'mai_veg_stock_sail', legacy: 'veg_stock_sail', name: 'Rudra Vega Stock Sell', icon: '🌒', segment: 'STOCK OPT SELL', accent: '#34d399', model: 'VEG' },
  // God 2026-10-27: ETFs split out of Rudra Sthambh (BOT-10); cash BUY-only, a sell only closes a held buy
  { id: 'mai_sthambh_etf',  legacy: 'sthambh_etf',  name: 'Rudra Sthambh ETF',   icon: '🏦',  segment: 'ETF BUY',      accent: '#f59e0b', model: 'STHAMBH' },
];

const BY_ID = new Map<string, BotIdentity>();
for (const b of FLEET) { BY_ID.set(b.id, b); BY_ID.set(b.legacy, b); }

/** Canonical identity for any bot id (canonical OR legacy), else null. */
export const botOf = (id: string): BotIdentity | null =>
  BY_ID.get(String(id || '').trim()) || null;

/**
 * The ONE display name for a bot id — every panel must call this.
 *
 * Unknown ids are NOT forced into the fleet vocabulary: a TradingView-created
 * bot carries an epoch-ms id and gets a short tag, and anything else is returned
 * readable-but-unchanged. Inventing a canonical name for a bot that is not in the
 * registry would hide exactly the drift this module exists to stop.
 */
export const botName = (id: string): string => {
  if (!id || id === 'ALL') return 'All Bots';
  const hit = botOf(id);
  if (hit) return hit.name;
  const raw = String(id).replace(/^bot_/, '');
  if (/^\d{6,}$/.test(raw)) return `TV Bot ·${raw.slice(-4)}`;
  return raw.replace(/_/g, ' ').toUpperCase();
};

/** Name with its icon — for cards and headers that show a glyph. */
export const botNameIcon = (id: string): string => {
  const b = botOf(id);
  return b ? `${b.icon} ${b.name}` : botName(id);
};

/** Canonical id for a display name (handles every historical spelling). */
const NAME_ALIASES: Record<string, string> = {
  // Every name this system has ever used, folded onto the canonical id, so old
  // rows, old configs and old logs keep resolving after the rename.
  'rudra vega': 'mai_veg', 'rudra vag': 'mai_veg', 'vega': 'mai_veg', 'mai veg': 'mai_veg',
  'rudra vega max': 'mai_veg_mcx', 'rudra vega mcx': 'mai_veg_mcx', 'vega mcx': 'mai_veg_mcx',
  'mai veg mcx': 'mai_veg_mcx', 'rudra vega max sell': 'mai_veg_mcx_sail',
  'rudra vega mcx option sell': 'mai_veg_mcx_sail', 'vega mcx sell': 'mai_veg_mcx_sail',
  'rudra vega sell': 'mai_veg_sail', 'rudra vega option sell': 'mai_veg_sail', 'vega sell': 'mai_veg_sail',
  'rudra shool': 'mai_shool', 'shool': 'mai_shool',
  'rudra shool max': 'mai_shool_mcx', 'rudra shool mcx': 'mai_shool_mcx', 'shool mcx': 'mai_shool_mcx',
  'rudra sthambh': 'mai_sthambh', 'sthambh': 'mai_sthambh',
  'rudra sthambh etf': 'mai_sthambh_etf', 'sthambh etf': 'mai_sthambh_etf',
  'rudra vega stock': 'mai_veg_stock', 'rudra vag stock': 'mai_veg_stock', 'vega stock': 'mai_veg_stock',
  'rudra vega stock sell': 'mai_veg_stock_sail', 'vega stock sell': 'mai_veg_stock_sail',
  'rudra shool stock': 'mai_shool_stock', 'shool stock': 'mai_shool_stock',
};
/** The 19 ETFs that moved from Rudra Sthambh to Rudra Sthambh ETF (God 2026-10-27); mirrors sthambh_dna_law.ETFS. */
export const STHAMBH_ETFS: readonly string[] = [
  'BANKBEES', 'BSE500IETF', 'FMCGIETF', 'GOLDBEES', 'HDFCNIF100', 'HNGSNGBEES', 'ITBEES', 'JUNIORBEES', 'LICNETFSEN',
  'LIQUIDBEES', 'NEXT30ADD', 'NIF100BEES', 'NIFTYBEES', 'PSUBNKBEES', 'SBISILVER', 'SETFGOLD', 'SETFNIF50', 'SETFNIFBK',
  'SILVERBEES',
];
/** Sthambh lane of a cash symbol: an ETF trades on mai_sthambh_etf, a stock on mai_sthambh. */
export const sthambhBotFor = (symbol: string): string =>
  STHAMBH_ETFS.includes(String(symbol || '').toUpperCase().replace(/[\s-]/g, '').replace(/_?EQ$/, '')) ? 'mai_sthambh_etf' : 'mai_sthambh';

export const botIdOfName = (name: string): string | null =>
  NAME_ALIASES[String(name || '').toLowerCase().replace(/\s+/g, ' ').trim()] || null;
